import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { customerApi, setCustomerToken, apiError } from "@/lib/customerApi";
import { SourcePicker } from "@/components/SourcePicker";
import StoreTopbar from "../StoreTopbar";
import HubFooter from "../HubFooter";

const COPY = {
  login: ["Welcome back", "Log in to manage your plan, bots and licence key.", "Log in"],
  register: ["Create your account", "Free to create. You only pay when you choose a plan.", "Create account"],
  forgot: ["Reset your password", "We'll email you a reset link (valid 1 hour).", "Send reset link"],
  reset: ["Choose a new password", "At least 8 characters.", "Save new password"],
};

const Input = (p) => (
  <input {...p} className="w-full bg-[#050914] border border-blue-900/60 focus:border-[#38BDF8] rounded-md px-4 py-3 text-sm text-[#F1F5F9] placeholder:text-[#475569] outline-none transition-colors" />
);

export default function AccountAuth({ mode }) {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [f, setF] = useState({ email: "", password: "", name: "", marketing_opt_in: true, accepted_terms: false, sources: [] });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const next = params.get("next") || "/account";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setMsg("");
    try {
      if (mode === "login" || mode === "register") {
        const body = mode === "login" ? { email: f.email, password: f.password } : f;
        const { data } = await customerApi.post(`/store/account/${mode}`, body);
        setCustomerToken(data.token);
        nav(next);
      } else if (mode === "forgot") {
        await customerApi.post("/store/account/forgot-password", { email: f.email, origin_url: window.location.origin });
        setMsg("If that email has an account, a reset link is on its way.");
      } else {
        await customerApi.post("/store/account/reset-password", { token: params.get("token"), password: f.password });
        setMsg("Password updated - you can log in now.");
      }
    } catch (ex) {
      setErr(apiError(ex));
    } finally {
      setBusy(false);
    }
  };

  const [h, sub, cta] = COPY[mode];
  return (
    <div data-testid={`account-${mode}-page`} className="min-h-screen bg-[#050914] text-[#F1F5F9]">
      <StoreTopbar subtitle="Your account" />
      <main className="max-w-md mx-auto px-6 py-16">
        <h1 className="text-3xl font-medium tracking-tight">{h}</h1>
        <p className="text-sm text-[#94A3B8] mt-2">{sub}</p>
        <form onSubmit={submit} className="mt-8 space-y-4" data-testid={`account-${mode}-form`}>
          {mode === "register" && <Input placeholder="First name" value={f.name} onChange={set("name")} data-testid="account-name-input" />}
          {mode !== "reset" && <Input type="email" required placeholder="you@email.com" value={f.email} onChange={set("email")} data-testid="account-email-input" />}
          {mode !== "forgot" && <Input type="password" required minLength={mode === "login" ? 1 : 8} placeholder="Password" value={f.password} onChange={set("password")} data-testid="account-password-input" />}
          {mode === "register" && (
            <div className="space-y-3 text-xs text-[#CBD5E1]">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={f.marketing_opt_in} onChange={set("marketing_opt_in")} data-testid="account-marketing-checkbox" className="mt-0.5 accent-[#FBBF24]" />
                Email me news, new bots and updates (unsubscribe any time).
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" required checked={f.accepted_terms} onChange={set("accepted_terms")} data-testid="account-terms-checkbox" className="mt-0.5 accent-[#FBBF24]" />
                <span>I have read the <Link to="/legal" className="underline">terms and risk disclosure</Link>. I understand trading is high risk, results shown are backtests, and this is not financial advice.</span>
              </label>
              <SourcePicker value={f.sources} onChange={(v) => setF({ ...f, sources: v })} testid="account-source" />
            </div>
          )}
          {err && <div data-testid="account-error" className="text-xs text-[#F87171]">{err}</div>}
          {msg && <div data-testid="account-message" className="text-xs text-[#2ECA7F]">{msg}</div>}
          <button type="submit" disabled={busy} data-testid="account-submit" className="w-full mono text-[11px] uppercase tracking-widest px-5 py-3 rounded-lg bg-[#FBBF24] text-[#050914] disabled:opacity-50 hover:scale-[1.01] transition-transform">
            {busy ? "…" : cta}
          </button>
        </form>
        <div className="mt-6 text-xs text-[#94A3B8] space-y-2">
          {mode === "login" && <>
            <div>No account? <Link to={`/account/register?next=${encodeURIComponent(next)}`} className="inline-block py-2 text-[#38BDF8]" data-testid="account-to-register">Create one</Link></div>
            <div><Link to="/account/forgot" className="inline-block py-2 text-[#38BDF8]" data-testid="account-to-forgot">Forgot password?</Link></div>
          </>}
          {mode !== "login" && <div>Have an account? <Link to="/account/login" className="text-[#38BDF8]" data-testid="account-to-login">Log in</Link></div>}
        </div>
      </main>
      <HubFooter />
    </div>
  );
}
